import { useState } from 'react';
import { Link } from 'react-router-dom';
import AuthForm from './AuthForm';
import WaitlistForm from './WaitlistForm';

// Minimal plate mark: two concentric rings in primary blue (Quiet Board brand).
function Logo({ className = 'w-8 h-8' }) {
  return (
    <svg viewBox="0 0 32 32" className={className} fill="none" aria-hidden="true">
      <circle cx="16" cy="16" r="13" stroke="var(--dl-primary)" strokeWidth="2" />
      <circle cx="16" cy="16" r="6.5" stroke="var(--dl-primary)" strokeWidth="2" />
    </svg>
  );
}

// Thin line icon wrapper (Lucide-style: 24 viewBox, currentColor, 2px stroke).
function LineIcon({ children, className = 'w-5 h-5' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}

// Design-system meal-source glyphs (Lucide), used at equal visual weight.
const SourceIcon = {
  home: (<><path d="M6 13.87A4 4 0 0 1 7.41 6a5.11 5.11 0 0 1 1.05-1.54 5 5 0 0 1 7.08 0A5.11 5.11 0 0 1 16.59 6 4 4 0 0 1 18 13.87V21H6Z" /><path d="M6 17h12" /></>),
  leftovers: (<><path d="m7.5 4.27 9 5.15" /><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" /></>),
  takeout: (<><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" /></>),
};

const FEATURES = [
  {
    title: 'Skip the blank week',
    description: 'Suppertide suggests dinners that fit your schedule, budget, and tastes. You approve every night.',
    icon: (<><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M8 2v4M16 2v4M3 10h18" /><path d="m9 16 2 2 4-4" /></>),
  },
  {
    title: 'Plan the real week',
    description: 'Home-cooked, leftovers, frozen, and takeout are all valid nights. No guilt, no failure states.',
    icon: (<><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /></>),
  },
  {
    title: 'Shopping and prep',
    description: 'Lock the week and get a grocery list by aisle and prep tasks by day.',
    icon: (<><circle cx="8" cy="21" r="1" /><circle cx="19" cy="21" r="1" /><path d="M2.5 3h2l2.6 12.4a2 2 0 0 0 2 1.6h9.7a2 2 0 0 0 2-1.6L23 7H5.6" /></>),
  },
  {
    title: 'Tonight, on your phone',
    description: "A calm view of what's for dinner tonight — the ingredients, the timing, and the steps.",
    icon: (<path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />),
  },
];

function FeatureCol({ icon, title, description }) {
  return (
    <div>
      <div className="w-11 h-11 rounded-card bg-surface-sunken text-ink-secondary flex items-center justify-center mb-3">
        <LineIcon>{icon}</LineIcon>
      </div>
      <h3 className="font-body text-base font-semibold text-ink mb-1">{title}</h3>
      <p className="font-body text-sm text-ink-secondary leading-relaxed">{description}</p>
    </div>
  );
}

function StepCard({ number, title, description }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary-tint text-primary-text font-mono text-sm font-medium flex items-center justify-center">
        {number}
      </div>
      <div>
        <h3 className="font-body text-lg font-semibold text-ink mb-1">{title}</h3>
        <p className="font-body text-ink-secondary leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

// Weekly plan showcase — a locked week with real-life sources at equal weight.
const PLAN_MEALS = [
  { name: 'Sheet-pan chicken', meta: 'Home cooked · Tonight', source: 'home' },
  { name: 'Leftovers night', meta: 'Leftovers · Wed', source: 'leftovers' },
  { name: 'Tacos to-go', meta: 'Takeout · Thu', source: 'takeout' },
];

function WeeklyPlanCard() {
  return (
    <div className="rounded-panel bg-surface border border-hairline p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-1.5 h-1.5 rounded-full bg-primary" />
        <span className="font-body text-sm font-semibold text-ink">This week&apos;s plan</span>
        <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-status-locked-bg border border-status-locked-border text-status-locked-text font-body text-[11px] font-medium">
          <LineIcon className="w-3 h-3"><rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></LineIcon>
          Locked
        </span>
      </div>

      <div className="space-y-2">
        {PLAN_MEALS.map((m) => (
          <div key={m.name} className="flex items-center gap-3 rounded-card px-3 py-2.5 border border-hairline">
            <span className="w-9 h-9 rounded-sm bg-surface-sunken text-ink-secondary flex items-center justify-center">
              <LineIcon className="w-4 h-4">{SourceIcon[m.source]}</LineIcon>
            </span>
            <div className="min-w-0">
              <p className="font-body text-sm font-medium text-ink truncate">{m.name}</p>
              <p className="font-body text-[11px] text-ink-muted">{m.meta}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-card p-3 bg-surface-sunken mt-4">
        <p className="font-body text-xs font-medium text-ink-secondary">Shopping list</p>
        <p className="font-body text-[11px] text-ink-muted mt-0.5">12 items, sorted by aisle</p>
      </div>

      <button
        type="button"
        className="w-full mt-4 py-3 rounded-btn bg-primary text-ink-inverse font-body font-medium hover:bg-primary-hover transition-colors"
      >
        See tonight&apos;s dinner
      </button>
    </div>
  );
}

// Landing CTA: waitlist-first, with sign-in tucked behind a toggle for
// already-invited testers.
function WaitlistOrSignin() {
  const [mode, setMode] = useState('waitlist'); // 'waitlist' | 'signin'
  return (
    <div>
      {mode === 'waitlist' ? <WaitlistForm /> : <AuthForm />}
      <button
        type="button"
        onClick={() => setMode(mode === 'waitlist' ? 'signin' : 'waitlist')}
        className="w-full mt-4 font-body text-sm text-ink-muted hover:text-ink-secondary transition-colors text-center"
      >
        {mode === 'waitlist' ? 'Already invited? Sign in' : '← Back to the waitlist'}
      </button>
    </div>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-canvas text-ink">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-surface/85 backdrop-blur-md border-b border-hairline">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Logo className="w-7 h-7" />
            <span className="font-serif text-xl text-ink">Suppertide</span>
          </div>
          <a
            href="#get-started"
            className="inline-flex items-center px-4 py-2 rounded-btn bg-primary text-ink-inverse font-body font-medium text-sm hover:bg-primary-hover transition-colors"
          >
            Join the waitlist
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section className="border-b border-hairline">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-20 sm:pt-24 sm:pb-24">
          <div className="lg:grid lg:grid-cols-2 lg:gap-14 lg:items-center">
            {/* Left: copy */}
            <div>
              <span className="inline-flex items-center px-3 py-1 rounded-full border border-hairline bg-surface font-body text-xs font-medium text-ink-secondary">
                A realistic week in about five minutes
              </span>
              <h1 className="font-serif text-ink leading-[1.02] text-5xl sm:text-6xl mt-5">
                Know what&apos;s for dinner this week
              </h1>
              <p className="font-body text-base sm:text-lg text-ink-secondary mt-5 max-w-md leading-relaxed">
                Suppertide builds a realistic week of dinners your household approves, then hands you the shopping list and tonight&apos;s steps. Busy nights, leftovers, and takeout all count as a plan.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <a
                  href="#get-started"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-btn bg-primary text-ink-inverse font-body font-medium hover:bg-primary-hover transition-colors"
                >
                  Join the waitlist
                </a>
                <a
                  href="#how-it-works"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-btn border border-border text-ink font-body font-medium hover:bg-surface-sunken transition-colors"
                >
                  See how it works
                </a>
              </div>
            </div>

            {/* Right: weekly plan card */}
            <div className="mt-12 lg:mt-0">
              <WeeklyPlanCard />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-b border-hairline py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {FEATURES.map((f) => (
              <FeatureCol key={f.title} icon={f.icon} title={f.title} description={f.description} />
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-20 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-body text-2xl sm:text-3xl font-semibold text-ink text-center mb-3">
            From blank week to locked plan
          </h2>
          <p className="font-body text-ink-secondary text-center mb-12 max-w-2xl mx-auto leading-relaxed">
            Three quick steps, about five minutes.
          </p>
          <div className="max-w-xl mx-auto space-y-8">
            <StepCard number="1" title="Set your week" description="Tag the busy nights, set a rough budget, and say who's eating." />
            <StepCard number="2" title="Review and approve" description="Suppertide fills in the week. Everyone reacts, you approve, and you can swap any night." />
            <StepCard number="3" title="Lock it in" description="Get your shopping list, prep tasks, and tonight's dinner. Then cook." />
          </div>
        </div>
      </section>

      {/* CTA / sign-up */}
      <section id="get-started" className="border-t border-hairline bg-surface-sunken py-20 scroll-mt-20">
        <div className="max-w-md mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <Logo className="w-12 h-12 mx-auto mb-4" />
            <h2 className="font-serif text-3xl text-ink mb-2">
              Ready for an easier week?
            </h2>
            <p className="font-body text-ink-secondary leading-relaxed">
              Suppertide is in early access. Join the waitlist and we&apos;ll email you when it&apos;s your turn.
            </p>
          </div>

          <WaitlistOrSignin />
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-hairline py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Logo className="w-5 h-5" />
              <span className="font-body text-sm text-ink-muted">© 2026 Suppertide</span>
            </div>
            <div className="flex gap-6">
              <Link to="/privacy" className="font-body text-sm text-ink-muted hover:text-ink transition-colors">Privacy policy</Link>
              <Link to="/terms" className="font-body text-sm text-ink-muted hover:text-ink transition-colors">Terms of service</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
